import { useQuery } from '@tanstack/react-query';

import {
  Api,
  OverVsUnderByPositionRequest,
  useApi,
} from '@sbv-clients/data-access-api';
import { formatDate, UnwrapArray } from '@sbv-clients/util-misc';

import { DashboardCacheKeys } from '../utils/cache-keys';

const calloffsReportGet =
  (
    api: Api,
    departmentId: number,
    positionId: number,
    startDate: string,
    endDate: string
  ) =>
  async () => {
    const safeDepartmentId: any = departmentId || null;
    const safePositionId: any = positionId || null;
    return api.dashboardApi
      .dashboardCalloffsGet({
        departmentId: safeDepartmentId || null,
        positionId: safePositionId || null,
        startDate,
        endDate,
      })
      .then((response) => response?.data?.data);
  };

export const useCallOffsReportGet = (
  departmentId: number,
  positionId: number,
  startDate: Date,
  endDate: Date
) => {
  const api = useApi();
  const start = formatDate(startDate);
  const end = formatDate(endDate);
  return useQuery({
    queryKey: [
      DashboardCacheKeys.CallOffsReport,
      { departmentId, positionId, start, end },
    ],
    queryFn: calloffsReportGet(api, departmentId, positionId, start, end),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export type CallOffAbsenceType = NonNullable<
  UnwrapArray<
    NonNullable<
      UnwrapArray<
        NonNullable<ReturnType<typeof useCallOffsReportGet>['data']>
      >['absences']
    >
  >
>;

const arrivalsGet =
  (api: Api, departmentId: number, positionId: number, payDate: string) =>
  async () => {
    const safeDepartmentId: any = departmentId || null;
    const safePositionId: any = positionId || null;
    const { arrivals = [], unscheduledArrivals = [] } = await api.dashboardApi
      .dashboardArrivalsGet({
        departmentId: safeDepartmentId,
        positionId: safePositionId,
        payDate,
      })
      .then((response) => response.data.data ?? {});

    const arrivalsResponse = [...(arrivals ?? [])];
    if (unscheduledArrivals?.length) {
      arrivalsResponse.push({
        startTime: 'Unscheduled',
        in: unscheduledArrivals.length,
      });
    }
    return {
      arrivalsResponse,
      unscheduledArrivals: unscheduledArrivals || [],
    };
  };

export const useArrivalsGet = (
  departmentId: number,
  positionId: number,
  payDate: Date
) => {
  const api = useApi();
  const date = formatDate(payDate);
  return useQuery({
    queryKey: [
      DashboardCacheKeys.Arrivals,
      { departmentId, positionId, payDate },
    ],
    queryFn: arrivalsGet(api, departmentId, positionId, date),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

type ArrivalsGetResponseType = NonNullable<
  UnwrapArray<ReturnType<typeof useArrivalsGet>['data']>
>;

export type ArrivalsResponseType = NonNullable<
  UnwrapArray<ArrivalsGetResponseType['arrivalsResponse']>
>;

export type ArrivalDepartureEmployeeType = NonNullable<
  UnwrapArray<ArrivalsResponseType['lateEmployees']>
>;

export type UnScheduledArrivalDepartureType = NonNullable<
  UnwrapArray<ArrivalsGetResponseType['unscheduledArrivals']>
>;

const departuresGet =
  (api: Api, departmentId: number, positionId: number, payDate: string) =>
  async () => {
    const safeDepartmentId: any = departmentId || null;
    const safePositionId: any = positionId || null;
    const { departures, unscheduledDepartures } = await api.dashboardApi
      .dashboardDeparturesGet({
        departmentId: safeDepartmentId,
        positionId: safePositionId,
        payDate,
      })
      .then((response) => response.data.data ?? {});
    const departuresResponse = [...(departures || [])];
    if (unscheduledDepartures?.length) {
      departuresResponse.push({
        endTime: 'Unscheduled',
        scheduled: 0,
        out: unscheduledDepartures.length,
      });
    }
    return {
      departuresResponse,
      unscheduledDepartures: unscheduledDepartures || [],
    };
  };

export const useDeparturesGet = (
  departmentId: number,
  positionId: number,
  payDate: Date
) => {
  const api = useApi();
  const date = formatDate(payDate);
  return useQuery({
    queryKey: [
      DashboardCacheKeys.Departures,
      { departmentId, positionId, payDate },
    ],
    queryFn: departuresGet(api, departmentId, positionId, date),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

type DeparturesGetResponseType = NonNullable<
  UnwrapArray<ReturnType<typeof useDeparturesGet>['data']>
>;

export type DeparturesResponseType = NonNullable<
  UnwrapArray<DeparturesGetResponseType['departuresResponse']>
>;

export const usePendingOpenshiftApprovalsGet = (
  departmentId: number,
  positionId: number
) => {
  const api = useApi();
  return useQuery({
    queryKey: [
      DashboardCacheKeys.PendingOpenshiftApprovals,
      {
        departmentId,
        positionId,
      },
    ],
    queryFn: async () => {
      const safeDepartmentId: any = departmentId || null;
      const safePositionId: any = positionId || null;
      return api.dashboardApi
        .dashboardPendingOpenshiftApprovalsGet({
          departmentId: safeDepartmentId,
          positionId: safePositionId,
        })
        .then((response) => ({
          openshiftsApprovals: response.data.data?.openshiftsApprovals ?? 0,
        }));
    },
    retry: false,
    refetchOnWindowFocus: false,
  });
};

const dashboardPendingAbsenceRequestsCountGet = (
  api: Api,
  departmentId?: number,
  positionId?: number
) => {
  return async () => {
    const safeDeptId: any = departmentId || null;
    const safePositionId: any = positionId || null;

    const response =
      await api.dashboardApi.dashboardPendingAbsenceRequestsCountGet({
        departmentId: safeDeptId,
        positionId: safePositionId,
      });

    return response.data;
  };
};

export const useDashboardAbsenceRequestsCountGet = (
  departmentId?: number,
  positionId?: number
) => {
  const api = useApi();
  return useQuery({
    queryKey: [
      DashboardCacheKeys.PendingRequestsCount,
      { departmentId, positionId },
    ],
    queryFn: dashboardPendingAbsenceRequestsCountGet(
      api,
      departmentId,
      positionId
    ),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useDashboardHppdHoursGet = (
  departmentId: number,
  positionId: number,
  startDate: string,
  endDate: string
) => {
  const api = useApi();
  const safeId: any = departmentId || null;
  const safePositionId: any = positionId || null;
  return useQuery({
    queryKey: [
      DashboardCacheKeys.HppdHours,
      { departmentId, positionId, startDate, endDate },
    ],
    queryFn: () =>
      api.dashboardApi
        .dashboardHppdHoursGet({
          departmentId: safeId,
          positionId: safePositionId,
          startDate,
          endDate,
        })
        .then((response) => response.data.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export type DashboardHppdHoursHourType = NonNullable<
  UnwrapArray<ReturnType<typeof useDashboardHppdHoursGet>['data']>
>;

export const useUnderOverByPositionGet = (
  overVsUnderByPositionRequest: OverVsUnderByPositionRequest
) => {
  const api = useApi();
  return useQuery({
    queryKey: [
      DashboardCacheKeys.OverVsUnderTable,
      overVsUnderByPositionRequest,
    ],
    queryFn: async () => {
      return api.dashboardApi
        .dashboardOverVsUnderByPositionPost({
          overVsUnderByPositionRequest: {
            ...overVsUnderByPositionRequest,
            positionId: overVsUnderByPositionRequest.positionId || null,
          },
        })
        .then((response) => response?.data?.data ?? []);
    },
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export type UnderOverPositionType = NonNullable<
  UnwrapArray<ReturnType<typeof useUnderOverByPositionGet>['data']>
>;

export const useTaDashboard = () => {
  const api = useApi();
  return useQuery({
    queryKey: [DashboardCacheKeys.TaDashboard],
    queryFn: () =>
      api.employeeV2Api.apiV2EmployeesTaDashboardGet().then((r) => r.data),
    retry: false,
    refetchOnWindowFocus: false,
  });
};
